import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Edit } from "lucide-react";
import LearningService, { Learning } from "../service/learningService";
import CategoryService, { Category } from "../../categories/service/categoryService";
import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { Pagination, DataTable } from "@/shared/widgets";
import { Input } from "@/shared/ui/input";
import { Search } from "lucide-react";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { useTranslation } from "@/shared/i18n";

const LearningList = () => {
    const { t } = useTranslation('learnings');
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [searchQuery, setSearchQuery] = useState("");

    // Debounce da pesquisa para evitar muitas requisições
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    // Buscar learnings com paginação
    const { data: learningsResponse, isLoading } = useCustomQuery({
        serviceInstance: LearningService.Instance,
        selectAction: (s) => s.getLearnings,
        parameter: { page: currentPage, limit: pageSize, search: debouncedSearchQuery || undefined },
    });

    // Buscar todas as categorias com limite alto para mapear IDs para nomes
    const { data: categoriesResponse } = useCustomQuery({
        serviceInstance: CategoryService.Instance,
        selectAction: (s) => s.getCategories,
        parameter: { limit: 9999, type: "LEARNING" },
    });

    const learnings = (learningsResponse as any)?.data || [];
    const totalPages = (learningsResponse as any)?.totalPage || 1;
    const totalItems = (learningsResponse as any)?.totalItems || 0;
    const categories = (categoriesResponse as any)?.data || [];


    // Criar mapa de categorias para busca rápida
    const categoryMap = useMemo(() => {
        const map = new Map<string, string>();
        categories.forEach((category: Category) => {
            if (category.id) {
                map.set(category.id, category.name);
            }
        });
        return map;
    }, [categories]);


    const getTypeDisplayName = (type: string) => {
        switch (type) {
            case "ARTICLE":
                return t('article');
            case "VIDEO":
                return t('video');
            case "COURSE":
                return t('course');
            case "TUTORIAL":
                return t('tutorial');
            default:
                return type;
        }
    };

    const getCategoryName = (categoryId?: string) => {

        if (!categoryId) return t('noCategory');
        const categoryName = categoryMap.get(categoryId);
        return categoryName || t('categoryNotFound');
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(Number(size));
        setCurrentPage(1);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        // Reset para primeira página ao pesquisar (será aplicado quando o debounce executar)
    };

    // Reset da página quando a pesquisa debounced muda
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchQuery]);

    return (
        <div className="space-y-4 ">
            {/* Barra de pesquisa */}
            <Card className='bg-white/[0.03] border-white/[0.06]'>
                <CardHeader>
                    <CardTitle>{t('filters')}</CardTitle>
                    <CardDescription>{t('searchDescription')}</CardDescription>
                </CardHeader>

                <CardContent className="flex items-center gap-4">
                    <div className="bg-[#1A1A1A] border border-white/6 flex h-12 w-full max-w-md items-center gap-3 px-4 rounded-lg border-white/10 focus-within:border-white/20 transition-colors">
                        <Search className="h-4 w-4 opacity-60 text-white/60 " />
                        <Input
                            placeholder={t('searchPlaceholder')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="flex-1  bg-transparent text-white/60 outline-none placeholder:text-white/60  ring-0 border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Tabela */}
            <DataTable
                data={learnings}
                isLoading={isLoading}
                emptyTitle={t('emptyTitle')}
                emptyDescription={(hasFilters) =>
                    hasFilters
                        ? t('emptyDescriptionWithFilter', { query: debouncedSearchQuery })
                        : t('emptyDescription')
                }
                hasFilters={Boolean(debouncedSearchQuery)}
                columns={[
                    {
                        header: t('cover'),
                        cell: (learning: Learning) => (
                            <div className="w-16 h-12 rounded-lg overflow-hidden bg-[#333333] flex items-center justify-center">
                                {learning.cover ? (
                                    <img
                                        src={learning.cover}
                                        alt={learning.name}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.style.display = 'none';
                                            target.nextElementSibling?.classList.remove('hidden');
                                        }}
                                    />
                                ) : null}
                                <div className={`text-gray-400 text-xs ${learning.cover ? 'hidden' : ''}`}>
                                    {learning.coverType === 'IMAGE' ? '📷' : '📄'}
                                </div>
                            </div>
                        ),
                    },
                    {
                        header: t('name'),
                        cell: (learning: Learning) => (
                            <div className="truncate max-w-xs" title={learning.name}>
                                <span className="text-white">{learning.name}</span>
                            </div>
                        ),
                    },
                    {
                        header: t('type'),
                        cell: (learning: Learning) => (
                            <Badge variant="default">
                                {getTypeDisplayName(learning.type)}
                            </Badge>
                        ),
                    },
                    {
                        header: t('category'),
                        cell: (learning: Learning) => (
                            <div className="truncate max-w-xs" title={getCategoryName(learning.categoryLearning)}>
                                <span className="text-white">{getCategoryName(learning.categoryLearning)}</span>
                            </div>
                        ),
                    },
                    {
                        header: t('status'),
                        cell: (learning: Learning) => (
                            <Badge variant={learning.published ? 'default' : 'secondary'}>
                                {learning.published ? t('published') : t('draft')}
                            </Badge>
                        ),
                    },
                    {
                        header: t('actions'),
                        cell: (learning: Learning) => (
                            <div className="flex justify-end gap-2">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => navigate(`/learning/edit/${learning.id}`, { state: { learning } })}
                                    title={t('edit')}
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                            </div>
                        ),
                        className: "text-right",
                    },
                ]}
            />

            {/* Paginação */}
            {
                !isLoading && learnings.length > 0 && (
                    <div className="mt-6 w-full">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            setCurrentPage={handlePageChange}
                            pageSize={pageSize}
                            onPageSizeChange={handlePageSizeChange}
                            showPageSizeOptions={true}
                            pageSizeOptions={[10, 20, 50, 100]}
                            showTotalItems={true}
                        />
                    </div>
                )
            }
        </div >
    );
};

export default LearningList;